import { PROACTIVE_SOURCES } from '../../supabase/functions/_shared/proactiveDomain';
import type { ProactiveFeed } from '@/lib/proactiveAssistant';

export const HOME_BRIEFING_TTL = 24 * 3600_000;
export interface ProactiveHomeSummary {
  control: 'off' | 'paused' | 'configured';
  cache: 'empty' | 'prepared' | 'expired';
  expiresAt: number[];
  generatedAt: number | null;
  origin: 'foreground' | 'background' | null;
}

/** Discard all content and identifiers: Home needs only count, expiry and provenance. */
export function proactiveHomeSummary(feed: ProactiveFeed, now: number): ProactiveHomeSummary {
  const off: ProactiveHomeSummary = {
    control: 'off', cache: 'empty', expiresAt: [], generatedAt: null, origin: null,
  };
  if (feed.preferences.enabled !== true) return off;
  const control = feed.preferences.automatic === true && feed.paid === true ? 'configured' : 'paused';
  const snapshot = feed.snapshot;
  const empty: ProactiveHomeSummary = { ...off, control };
  if (!snapshot) return empty;
  const generatedAt = typeof snapshot.generated_at === 'string' ? Date.parse(snapshot.generated_at) : NaN;
  if (!Number.isFinite(generatedAt) || generatedAt > now || generatedAt + HOME_BRIEFING_TTL <= now ||
    !Array.isArray(snapshot.items) || !Array.isArray(snapshot.sources)) {
    return { ...empty, cache: 'expired' };
  }
  const consented = Array.isArray(feed.preferences.sources) ? feed.preferences.sources : [];
  const seen = new Set<string>();
  const expiresAt = snapshot.items.slice(0, 30).flatMap((item) => {
    if (!item || typeof item.id !== 'string' || !/^[0-9a-f]{64}$/.test(item.id) ||
      seen.has(item.id) || !PROACTIVE_SOURCES.includes(item.source) || !consented.includes(item.source) ||
      !snapshot.sources.some((source) => source && source.status === 'ready' &&
        source.source === item.source && (source.provider === item.provider ||
          (item.source === 'files' && source.provider === 'files' &&
            ['google_drive', 'onedrive', 'notion'].includes(item.provider))))) return [];
    const expiry = typeof item.expires_at === 'string' ? Date.parse(item.expires_at) : NaN;
    if (!Number.isFinite(expiry) || expiry <= now || expiry <= generatedAt ||
      expiry > generatedAt + HOME_BRIEFING_TTL) return [];
    seen.add(item.id);
    return [expiry];
  });
  return {
    control,
    cache: expiresAt.length ? 'prepared' : snapshot.items.length ? 'expired' : 'empty',
    expiresAt, generatedAt,
    // Historical foreground snapshots omit origin. Never infer an unknown origin.
    origin: snapshot.origin === 'background' ? 'background'
      : snapshot.origin === 'foreground' || snapshot.origin === undefined ? 'foreground' : null,
  };
}

export function currentHomeSummary(summary: ProactiveHomeSummary, now: number): ProactiveHomeSummary {
  if (summary.generatedAt !== null &&
    (summary.generatedAt > now || summary.generatedAt + HOME_BRIEFING_TTL <= now)) {
    return { ...summary, cache: 'expired', expiresAt: [], generatedAt: null, origin: null };
  }
  const expiresAt = summary.expiresAt.filter((expiry) => expiry > now);
  return {
    ...summary, expiresAt,
    cache: summary.cache === 'prepared' && !expiresAt.length ? 'expired' : summary.cache,
  };
}
